import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash, FloppyDisk, FilePdf, FileXls, FolderOpen,
  Receipt, Storefront, Package, Coins, ChartLine, Sparkle,
  ArrowsClockwise, Notepad, Eye, Tag, BookmarkSimple,
  Percent, Truck, Bank, CurrencyInr,
} from "@phosphor-icons/react";

import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import RecipePickerDialog from "../components/RecipePickerDialog";
import { api, API, fmtINR } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const TOKEN_KEY = "kostra:token";

const CATEGORY_SUGGESTIONS = [
  "Starters", "Mains", "Sides", "Desserts", "Beverages", "Snacks", "Specials",
];

const blankItem = (overrides = {}) => ({
  id: crypto.randomUUID(),
  recipe_id: "",
  dish_name: "",
  category: "",
  markup_mode: "percent",
  markup_value: 80,
  platform_fee_mode: "percent",
  platform_fee_value: 0,
  swiggy_fee_mode: "percent",
  swiggy_fee_value: 22,
  zomato_fee_mode: "percent",
  zomato_fee_value: 22,
  packaging_per_portion: 15,
  food_cost_override: "",
  discount_mode: "percent",
  discount_value: 0,
  delivery_fee: 0,
  gst_pct: 5,
  best_market_price: 0,
  min_portions_per_batch: 0,
  per_batch_cost: 0,
  bulk_order_discount_pct: 0,
  bulk_order_quantity: 0,
  bulk_order_rate: 0,
  ...overrides,
});

export default function MenuCreatorPage() {
  const { isAdmin, openLogin } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [name, setName] = useState("");
  const [outlet, setOutlet] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [editingId, setEditingId] = useState(null);
  const [hydrated, setHydrated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // ---- bootstrap -------------------------------------------------------
  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [r, m] = await Promise.all([
        api.get("/recipes"),
        api.get("/menus"),
      ]);
      setRecipes(r.data || []);
      setMenus(m.data || []);
    } catch (e) {
      toast.error("Failed to load recipes/menus");
    }
  }, [isAdmin]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- live pricing preview -------------------------------------------
  // Ensure optional numeric overrides ("" from empty Input) become null so
  // Pydantic accepts them.
  const normalizeItem = (it) => {
    const fco = it.food_cost_override;
    const clean = (fco === "" || fco == null) ? null : Number(fco);
    return { ...it, food_cost_override: (clean != null && clean > 0) ? clean : null };
  };

  const runPreview = useCallback(async () => {
    if (!isAdmin) return;
    const validItems = items.filter((it) => it.recipe_id).map(normalizeItem);
    if (!validItems.length) { setHydrated(null); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/menus/preview", {
        name: name || "Untitled Menu",
        outlet, notes, items: validItems,
      });
      setHydrated(data);
    } catch (e) {
      // silent — keep last good preview
    } finally { setPreviewing(false); }
  }, [items, name, outlet, notes, isAdmin]);

  useEffect(() => {
    const t = setTimeout(runPreview, 250);
    return () => clearTimeout(t);
  }, [runPreview]);

  // ---- item helpers ----------------------------------------------------
  const setItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setItems((arr) => [...arr, blankItem()]);

  // Bulk-add from Browse My Recipes picker
  const addFromPicker = (chosenRecipes) => {
    if (!chosenRecipes?.length) return;
    setItems((arr) => {
      // Drop a single empty row when present so picker results land cleanly
      const startsEmpty = arr.length === 1 && !arr[0].recipe_id;
      const base = startsEmpty ? [] : arr;
      const additions = chosenRecipes.map((r) => blankItem({
        recipe_id: r.id,
        dish_name: r.dish_name,
        category: r.category || "",
      }));
      return [...base, ...additions];
    });
    toast.success(`Added ${chosenRecipes.length} dish${chosenRecipes.length === 1 ? "" : "es"} to the menu`);
  };

  const pickRecipe = (idx, recipe_id) => {
    const rec = recipes.find((r) => r.id === recipe_id);
    setItems((arr) => arr.map((it, i) => i === idx ? {
      ...it,
      recipe_id,
      dish_name: rec?.dish_name || "",
      // Auto-fill the menu category from the recipe if user hasn't set one yet.
      category: it.category || rec?.category || "",
    } : it));
  };

  // ---- persistence -----------------------------------------------------
  const resetForm = () => {
    setEditingId(null);
    setName(""); setOutlet(""); setNotes("");
    setItems([blankItem()]);
    setHydrated(null);
  };

  const loadMenu = (m) => {
    setEditingId(m.id);
    setName(m.name || "");
    setOutlet(m.outlet || "");
    setNotes(m.notes || "");
    setItems((m.items || []).map((it) => {
      // Auto-migrate legacy single platform_fee_* to Swiggy + Zomato when
      // the new per-platform fields are missing/empty.
      const legacyPf = Number(it.platform_fee_value || 0);
      const legacyMode = it.platform_fee_mode || "amount";
      const sValue = it.swiggy_fee_value != null ? Number(it.swiggy_fee_value) : (legacyPf > 0 ? legacyPf : 0);
      const sMode = it.swiggy_fee_mode || (legacyPf > 0 ? legacyMode : "percent");
      const zValue = it.zomato_fee_value != null ? Number(it.zomato_fee_value) : (legacyPf > 0 ? legacyPf : 0);
      const zMode = it.zomato_fee_mode || (legacyPf > 0 ? legacyMode : "percent");
      return {
        id: it.id || crypto.randomUUID(),
        recipe_id: it.recipe_id,
        dish_name: it.dish_name || "",
        category: it.category || "",
        markup_mode: it.markup_mode || "amount",
        markup_value: Number(it.markup_value || 0),
        platform_fee_mode: legacyMode,
        platform_fee_value: legacyPf,
        swiggy_fee_mode: sMode,
        swiggy_fee_value: sValue,
        zomato_fee_mode: zMode,
        zomato_fee_value: zValue,
        packaging_per_portion: Number(it.packaging_per_portion || 0),
        food_cost_override: it.food_cost_override != null && Number(it.food_cost_override) > 0
          ? Number(it.food_cost_override) : "",
        discount_mode: it.discount_mode || "amount",
        discount_value: Number(it.discount_value || 0),
        delivery_fee: Number(it.delivery_fee || 0),
        gst_pct: Number(it.gst_pct || 0),
        best_market_price: Number(it.best_market_price || 0),
        min_portions_per_batch: Number(it.min_portions_per_batch || 0),
        per_batch_cost: Number(it.per_batch_cost || 0),
        bulk_order_discount_pct: Number(it.bulk_order_discount_pct || 0),
        bulk_order_quantity: Number(it.bulk_order_quantity || 0),
        bulk_order_rate: Number(it.bulk_order_rate || 0),
      };
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`Loaded "${m.name}"`);
  };

  const saveMenu = async () => {
    if (!name.trim()) { toast.error("Menu name is required"); return; }
    const validItems = items.filter((it) => it.recipe_id).map(normalizeItem);
    if (!validItems.length) { toast.error("Add at least one dish"); return; }
    setSaving(true);
    try {
      // Bulk Order Rate & Per Batch Cost are derived deterministically by the
      // backend from Selling Price × Min Portions and Food Cost × Min Portions.
      // Saved values are stored only to keep PDF/Excel/reload consistent with
      // the UI without re-hitting the recipe master.
      const payload = { name: name.trim(), outlet, notes, items: validItems };
      const url = editingId ? `/menus/${editingId}` : `/menus`;
      const method = editingId ? "put" : "post";
      const { data } = await api[method](url, payload);
      setEditingId(data.id);
      setHydrated(data);
      toast.success(editingId ? "Menu updated" : "Menu saved");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteMenu = async (id) => {
    if (!window.confirm("Delete this menu? This cannot be undone.")) return;
    try {
      await api.delete(`/menus/${id}`);
      toast.success("Menu deleted");
      if (editingId === id) resetForm();
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const downloadExport = async (fmt) => {
    if (!editingId) {
      toast.info("Save the menu first to download it");
      return;
    }
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/menus/${editingId}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOSTRA_Menu_${(name || "menu").replace(/[^\w-]+/g, "_")}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Download failed"); }
  };

  const totals = hydrated?.totals || {
    food_cost_total: 0, markup_total: 0, packaging_total: 0,
    platform_fee_total: 0, selling_price_total: 0, gross_profit_total: 0,
    blended_food_cost_pct: 0, item_count: 0,
    discount_total: 0, delivery_total: 0, gst_total: 0, net_selling_price_total: 0,
    net_sale_price_total: 0,
    swiggy_fee_total: 0, zomato_fee_total: 0,
    gross_profit_swiggy_total: 0, gross_profit_zomato_total: 0,
  };
  const hydratedItems = hydrated?.items || [];

  // ---- Guest CTA -------------------------------------------------------
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <Notepad size={14} weight="fill" /> Menu Creator
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Build a priced menu. <br className="hidden md:block" />
              <span className="text-[#D4AF37]">Export it. Sell it.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Pull recipes from your library, add markup, platform fees and packaging
              — KOSTRA computes the selling price + food cost % + gross profit per dish.
              PDF &amp; Excel exports included.
            </p>
            <Button onClick={() => openLogin()} className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                    data-testid="menu-guest-login-btn">
              Admin login to build a menu
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
            <Notepad size={14} weight="fill" /> Menu Creator · Live Pricing
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium mt-2 leading-tight">
            Build a priced menu — <span className="text-[#D4AF37]">live food-cost, markup, platform fee &amp; selling price.</span>
          </h1>
          <p className="text-white/70 text-sm mt-2 max-w-3xl">
            Pulls food cost per portion from your saved recipes. Toggle markup and
            platform fee between flat ₹ and % per row. Download as PDF / Excel anytime.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-14 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Editor column */}
        <div className="space-y-5">
          {/* Menu metadata card */}
          <div className="kostra-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-5">
                <Label className="kostra-data-label mb-1.5 block">Menu Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Summer Lunch Menu"
                       className="h-11" data-testid="menu-name-input" />
              </div>
              <div className="md:col-span-4">
                <Label className="kostra-data-label mb-1.5 block">Outlet / Brand</Label>
                <Input value={outlet} onChange={(e) => setOutlet(e.target.value)}
                       placeholder="e.g. The Cloud Kitchen Co."
                       className="h-11" data-testid="menu-outlet-input" />
              </div>
              <div className="md:col-span-3 flex items-end gap-2">
                {editingId && (
                  <Badge variant="outline" className="border-[#D4AF37] text-[#A87C0E] bg-[#FDF9EE] px-2 py-1.5 font-semibold">
                    Editing
                  </Badge>
                )}
                <Button onClick={resetForm} variant="outline" size="sm"
                        className="border-[#0A3B24]/20 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                        data-testid="menu-reset-btn">
                  <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> New
                </Button>
              </div>
              <div className="md:col-span-12">
                <Label className="kostra-data-label mb-1.5 block">Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          rows={2} placeholder="Optional — internal notes, tagline, valid-from date…"
                          data-testid="menu-notes-input" />
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="kostra-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="kostra-section-title text-lg flex items-center gap-2">
                <Notepad size={16} weight="duotone" className="text-[#D4AF37]" />
                Menu Items
              </h2>
              <div className="flex items-center gap-2">
                <Button onClick={() => setPickerOpen(true)} variant="outline" size="sm"
                        className="border-[#D4AF37] text-[#A87C0E] hover:bg-[#FDF9EE] font-semibold"
                        data-testid="menu-browse-recipes-btn">
                  <BookmarkSimple size={14} weight="bold" className="mr-1" /> Browse My Recipes
                </Button>
                <Button onClick={addItem} variant="outline" size="sm"
                        className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                        data-testid="menu-add-item-btn">
                  <Plus size={14} weight="bold" className="mr-1" /> Add Empty Row
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <AnimatePresence initial={false}>
                {items.map((it, idx) => {
                  const live = hydratedItems[idx]?.recipe_id === it.recipe_id ? hydratedItems[idx] : null;
                  const p = live?.pricing;
                  // Recipe's own computed food cost (before any menu-level override)
                  const recipe = recipes.find((r) => r.id === it.recipe_id);
                  const recipeFC = recipe ? Number(recipe.summary?.cost_per_portion || 0) : 0;
                  const hasOverride = it.food_cost_override !== "" && it.food_cost_override != null && Number(it.food_cost_override) > 0;
                  return (
                    <motion.div
                      key={it.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      className="rounded-xl border-2 border-[#E5E7EB] bg-white p-4"
                      data-testid={`menu-row-${idx}`}
                    >
                      {/* Header row */}
                      <div className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-12 md:col-span-5">
                          <Label className="kostra-data-label mb-1 block">Dish</Label>
                          <Select value={it.recipe_id || ""} onValueChange={(v) => pickRecipe(idx, v)}>
                            <SelectTrigger className="h-11" data-testid={`menu-recipe-select-${idx}`}>
                              <SelectValue placeholder="Pick from My Recipes…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                              {recipes.length === 0 && (
                                <div className="px-3 py-2 text-xs text-[#4B5563]">No saved recipes yet. Save one from the Calculator first.</div>
                              )}
                              {recipes.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.dish_name}{r.category ? ` · ${r.category}` : ""} · ₹{Number(r.summary?.cost_per_portion || 0).toFixed(0)}/portion
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-12 md:col-span-3">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Tag size={11} weight="bold" /> Menu Section
                          </Label>
                          <Input
                            list={`cat-suggest-${it.id}`}
                            value={it.category || ""}
                            onChange={(e) => setItem(idx, { category: e.target.value })}
                            placeholder="e.g. Starters"
                            className="h-11"
                            data-testid={`menu-category-${idx}`}
                          />
                          <datalist id={`cat-suggest-${it.id}`}>
                            {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                          </datalist>
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Coins size={11} weight="bold" /> Food Cost
                            {hasOverride && (
                              <button
                                type="button"
                                onClick={() => setItem(idx, { food_cost_override: "" })}
                                title={`Reset to recipe cost (₹${recipeFC.toFixed(2)})`}
                                className="ml-1 text-[9px] font-bold text-[#A87C0E] hover:text-[#0A3B24] underline decoration-dotted"
                                data-testid={`menu-food-cost-reset-${idx}`}
                              >
                                reset
                              </button>
                            )}
                          </Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#4B5563] font-mono-num">₹</span>
                            <Input
                              type="number" min="0" step="0.01"
                              value={it.food_cost_override}
                              onChange={(e) => setItem(idx, { food_cost_override: e.target.value })}
                              placeholder={recipeFC > 0 ? recipeFC.toFixed(2) : "0.00"}
                              className={`h-11 pl-5 font-mono-num font-bold text-sm text-[#0A3B24] ${
                                hasOverride ? "border-2 border-[#D4AF37] bg-[#FDF9EE]" : ""
                              }`}
                              title={hasOverride
                                ? `Manual override. Recipe cost is ₹${recipeFC.toFixed(2)}`
                                : "Uses recipe's computed food cost. Type a value here to override."}
                              data-testid={`menu-food-cost-${idx}`}
                            />
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-1">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Receipt size={11} weight="bold" /> SP
                          </Label>
                          <div className="h-11 inline-flex items-center w-full rounded-md border-2 border-[#D4AF37] bg-[#FDF9EE] px-2 font-mono-num font-bold text-[#0A3B24] text-sm"
                               data-testid={`menu-selling-${idx}`}>
                            ₹{p ? Number(p.selling_price).toFixed(0) : "—"}
                          </div>
                        </div>
                        <div className="col-span-12 md:col-span-1 flex justify-end">
                          <button type="button" onClick={() => removeItem(idx)}
                                  className="h-11 w-11 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center border border-transparent hover:border-[#FECACA]"
                                  data-testid={`menu-remove-${idx}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Cost build-up row */}
                      <div className="mt-3 grid grid-cols-12 gap-3 items-end">
                        {/* Markup */}
                        <div className="col-span-12 md:col-span-6">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <ChartLine size={11} weight="bold" /> Markup / portion
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="number" min="0" step="0.5"
                              value={it.markup_value}
                              onChange={(e) => setItem(idx, { markup_value: parseFloat(e.target.value || 0) })}
                              className="h-11 font-mono-num flex-1"
                              data-testid={`menu-markup-value-${idx}`}
                            />
                            <Select value={it.markup_mode}
                                    onValueChange={(v) => setItem(idx, { markup_mode: v })}>
                              <SelectTrigger className="h-11 w-20" data-testid={`menu-markup-mode-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="amount">₹</SelectItem>
                                <SelectItem value="percent">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {p && (
                            <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                              = ₹{Number(p.markup_amount).toFixed(2)}
                            </div>
                          )}
                        </div>

                        {/* Packaging */}
                        <div className="col-span-12 md:col-span-6">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Package size={11} weight="bold" /> Packaging / portion (₹)
                          </Label>
                          <Input
                            type="number" min="0" step="0.5"
                            value={it.packaging_per_portion}
                            onChange={(e) => setItem(idx, { packaging_per_portion: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-pkg-${idx}`}
                          />
                        </div>
                      </div>

                      {/* Platform fees row — Swiggy + Zomato, charged on Net Sale Price */}
                      <div className="mt-3 grid grid-cols-12 gap-3 items-end">
                        {/* Swiggy fee */}
                        <div className="col-span-12 md:col-span-6">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Storefront size={11} weight="bold" /> Swiggy Fee <span className="text-[9px] text-[#4B5563] normal-case font-medium">(on Net Sale Price)</span>
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="number" min="0" step="0.5"
                              value={it.swiggy_fee_value}
                              onChange={(e) => setItem(idx, { swiggy_fee_value: parseFloat(e.target.value || 0) })}
                              className="h-11 font-mono-num flex-1"
                              data-testid={`menu-swiggy-value-${idx}`}
                            />
                            <Select value={it.swiggy_fee_mode}
                                    onValueChange={(v) => setItem(idx, { swiggy_fee_mode: v })}>
                              <SelectTrigger className="h-11 w-20" data-testid={`menu-swiggy-mode-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="amount">₹</SelectItem>
                                <SelectItem value="percent">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {p && (
                            <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                              = ₹{Number(p.swiggy_fee_amount || 0).toFixed(2)}
                            </div>
                          )}
                        </div>

                        {/* Zomato fee */}
                        <div className="col-span-12 md:col-span-6">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Storefront size={11} weight="bold" /> Zomato Fee <span className="text-[9px] text-[#4B5563] normal-case font-medium">(on Net Sale Price)</span>
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="number" min="0" step="0.5"
                              value={it.zomato_fee_value}
                              onChange={(e) => setItem(idx, { zomato_fee_value: parseFloat(e.target.value || 0) })}
                              className="h-11 font-mono-num flex-1"
                              data-testid={`menu-zomato-value-${idx}`}
                            />
                            <Select value={it.zomato_fee_mode}
                                    onValueChange={(v) => setItem(idx, { zomato_fee_mode: v })}>
                              <SelectTrigger className="h-11 w-20" data-testid={`menu-zomato-mode-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="amount">₹</SelectItem>
                                <SelectItem value="percent">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {p && (
                            <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                              = ₹{Number(p.zomato_fee_amount || 0).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Online sale modifiers row */}
                      <div className="mt-3 grid grid-cols-12 gap-3 items-end">
                        {/* Discount */}
                        <div className="col-span-12 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Percent size={11} weight="bold" /> Online Discount / portion
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="number" min="0" step="0.5"
                              value={it.discount_value}
                              onChange={(e) => setItem(idx, { discount_value: parseFloat(e.target.value || 0) })}
                              className="h-11 font-mono-num flex-1"
                              data-testid={`menu-discount-value-${idx}`}
                            />
                            <Select value={it.discount_mode}
                                    onValueChange={(v) => setItem(idx, { discount_mode: v })}>
                              <SelectTrigger className="h-11 w-20" data-testid={`menu-discount-mode-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="amount">₹</SelectItem>
                                <SelectItem value="percent">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {p && p.discount_amount > 0 && (
                            <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                              = − ₹{Number(p.discount_amount).toFixed(2)}
                            </div>
                          )}
                        </div>
                        {/* Delivery fee */}
                        <div className="col-span-12 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Truck size={11} weight="bold" /> Delivery Fee / portion (₹)
                          </Label>
                          <Input
                            type="number" min="0" step="0.5"
                            value={it.delivery_fee}
                            onChange={(e) => setItem(idx, { delivery_fee: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-delivery-${idx}`}
                          />
                        </div>
                        {/* GST */}
                        <div className="col-span-12 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Bank size={11} weight="bold" /> GST % (on post-discount SP)
                          </Label>
                          <Input
                            type="number" min="0" max="28" step="0.5"
                            value={it.gst_pct}
                            onChange={(e) => setItem(idx, { gst_pct: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-gst-${idx}`}
                          />
                          {p && p.gst_amount > 0 && (
                            <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                              = + ₹{Number(p.gst_amount).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Market intel · Bulk inputs (3 fields → Bulk Order Rate/Pack Price/GP derived) */}
                      <div className="mt-3 grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-12 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <ChartLine size={11} weight="bold" /> Best Market Price (₹)
                          </Label>
                          <Input
                            type="number" min="0" step="0.5"
                            value={it.best_market_price}
                            onChange={(e) => setItem(idx, { best_market_price: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-best-market-${idx}`}
                          />
                          {p && Number(it.best_market_price) > 0 && (
                            <div className="text-[10px] mt-1 font-mono-num">
                              {Number(p.selling_price) < Number(it.best_market_price) ? (
                                <span className="text-emerald-700">
                                  Listed ₹{(it.best_market_price - p.selling_price).toFixed(2)} below market
                                </span>
                              ) : Number(p.selling_price) > Number(it.best_market_price) ? (
                                <span className="text-amber-700">
                                  Listed ₹{(p.selling_price - it.best_market_price).toFixed(2)} above market
                                </span>
                              ) : (
                                <span className="text-[#4B5563]">At market</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Package size={11} weight="bold" /> Min Portions per Batch
                          </Label>
                          <Input
                            type="number" min="0" step="1"
                            value={it.min_portions_per_batch}
                            onChange={(e) => setItem(idx, { min_portions_per_batch: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-min-portions-${idx}`}
                          />
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <Label className="kostra-data-label mb-1 block inline-flex items-center gap-1">
                            <Package size={11} weight="bold" /> Bulk Order Qty
                          </Label>
                          <Input
                            type="number" min="0" step="1"
                            value={it.bulk_order_quantity}
                            onChange={(e) => setItem(idx, { bulk_order_quantity: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            data-testid={`menu-bulk-qty-${idx}`}
                          />
                        </div>
                      </div>

                      {/* Derived bulk readouts — Per Batch Cost · Bulk Order Rate · Pack Price · GP */}
                      {(() => {
                        const minPP = Number(it.min_portions_per_batch || 0);
                        const qty = Number(it.bulk_order_quantity || 0);
                        const sp = p ? Number(p.selling_price || 0) : 0;
                        const fcPerPortion = p ? Number(p.food_cost_per_portion || 0) : 0;
                        const perBatchCost = Number((fcPerPortion * minPP).toFixed(2));
                        const bulkRate = Number((sp * minPP).toFixed(2));
                        const packPrice = Number((bulkRate * qty).toFixed(2));
                        const gpOnBulk = Number(((bulkRate - perBatchCost) * qty).toFixed(2));
                        const showBulk = minPP > 0 || qty > 0;
                        if (!showBulk) return null;
                        return (
                          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 rounded-lg bg-[#FDF9EE]/40 border border-dashed border-[#D4AF37]/40 p-2.5">
                            <div data-testid={`menu-per-batch-cost-derived-${idx}`}>
                              <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Per Batch Cost</div>
                              <div className="font-mono-num font-bold text-sm text-[#0A3B24]">
                                ₹{perBatchCost.toFixed(2)}
                              </div>
                              <div className="text-[10px] text-[#4B5563] font-mono-num">
                                = ₹{fcPerPortion.toFixed(2)} × {minPP}
                              </div>
                            </div>
                            <div data-testid={`menu-bulk-rate-derived-${idx}`}>
                              <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Bulk Order Rate</div>
                              <div className="font-mono-num font-bold text-sm text-[#A87C0E]">
                                ₹{bulkRate.toFixed(2)}
                              </div>
                              <div className="text-[10px] text-[#4B5563] font-mono-num">
                                = ₹{sp.toFixed(2)} × {minPP}
                              </div>
                            </div>
                            <div data-testid={`menu-bulk-pack-price-${idx}`}>
                              <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Bulk Pack Price</div>
                              <div className="font-mono-num font-bold text-sm text-emerald-700">
                                ₹{packPrice.toFixed(2)}
                              </div>
                              <div className="text-[10px] text-[#4B5563] font-mono-num">
                                = {qty} × ₹{bulkRate.toFixed(2)}
                              </div>
                            </div>
                            {qty > 0 && (
                              <div data-testid={`menu-gp-on-bulk-${idx}`}>
                                <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">GP on Bulk Order Qty</div>
                                <div className={`font-mono-num font-bold text-sm ${gpOnBulk >= 0 ? "text-emerald-700" : "text-amber-700"}`}>
                                  ₹{gpOnBulk.toFixed(2)}
                                </div>
                                <div className="text-[10px] text-[#4B5563] font-mono-num">
                                  = (₹{bulkRate.toFixed(2)} − ₹{perBatchCost.toFixed(2)}) × {qty}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* P&L strip */}
                      {p && (
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2 rounded-lg bg-[#FDF9EE]/60 border border-[#D4AF37]/30 p-2.5">
                          <PnLChip label="Food Cost" value={`₹${Number(p.food_cost_per_portion).toFixed(0)}`} />
                          <PnLChip label="Food Cost %" value={`${p.food_cost_pct.toFixed(1)}%`}
                                   accent={p.food_cost_pct > 40 ? "warn" : "ok"} />
                          <PnLChip label="Listed SP" value={`₹${Number(p.selling_price).toFixed(2)}`} accent="gold" />
                          <PnLChip label="Net Sale Price" value={`₹${Number(p.net_sale_price ?? (p.selling_price - (p.discount_amount || 0))).toFixed(2)}`} accent="gold" />
                          <PnLChip label="GP Swiggy" value={`₹${Number(p.gross_profit_swiggy ?? p.gross_profit_per_portion).toFixed(2)}`}
                                   accent={(p.gross_profit_swiggy ?? p.gross_profit_per_portion) >= 0 ? "ok" : "warn"}
                                   testid={`menu-gp-swiggy-${idx}`} />
                          <PnLChip label="GP Zomato" value={`₹${Number(p.gross_profit_zomato ?? p.gross_profit_per_portion).toFixed(2)}`}
                                   accent={(p.gross_profit_zomato ?? p.gross_profit_per_portion) >= 0 ? "ok" : "warn"}
                                   testid={`menu-gp-zomato-${idx}`} />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>

          {/* Totals + actions */}
          <div className="kostra-card p-5">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="menu-totals">
              <TotalStat label="Items" value={totals.item_count} />
              <TotalStat label="Food Cost Σ" value={fmtINR(totals.food_cost_total)} />
              <TotalStat label="Listed SP Σ" value={fmtINR(totals.selling_price_total)} accent="gold" />
              <TotalStat label="Net Sale Price Σ" value={fmtINR(totals.net_sale_price_total || totals.selling_price_total)} accent="gold" />
              <TotalStat label="GP Swiggy Σ" value={fmtINR(totals.gross_profit_swiggy_total ?? totals.gross_profit_total)} accent="ok" testid="totals-gp-swiggy" />
              <TotalStat label="GP Zomato Σ" value={fmtINR(totals.gross_profit_zomato_total ?? totals.gross_profit_total)} accent="ok" testid="totals-gp-zomato" />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[#4B5563]">
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-2.5 py-1.5">
                <span className="font-bold uppercase tracking-wider text-[10px]">Swiggy Fee Σ</span>
                <span className="font-mono-num ml-2 text-[#0A3B24]">{fmtINR(totals.swiggy_fee_total || 0)}</span>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-2.5 py-1.5">
                <span className="font-bold uppercase tracking-wider text-[10px]">Zomato Fee Σ</span>
                <span className="font-mono-num ml-2 text-[#0A3B24]">{fmtINR(totals.zomato_fee_total || 0)}</span>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-2.5 py-1.5">
                <span className="font-bold uppercase tracking-wider text-[10px]">Blended FC%</span>
                <span className="font-mono-num ml-2 text-emerald-700">{(totals.blended_food_cost_pct || 0).toFixed(1)}%</span>
              </div>
              <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-2.5 py-1.5">
                <span className="font-bold uppercase tracking-wider text-[10px]">Net SP (cust.) Σ</span>
                <span className="font-mono-num ml-2 text-[#0A3B24]">{fmtINR(totals.net_selling_price_total || 0)}</span>
              </div>
            </div>
            {(totals.discount_total > 0 || totals.delivery_total > 0 || totals.gst_total > 0) && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-[#4B5563]">
                {totals.discount_total > 0 && (
                  <div className="rounded-md bg-[#FDF9EE]/40 border border-[#D4AF37]/20 px-2.5 py-1.5">
                    <span className="font-bold uppercase tracking-wider text-[10px]">Discount Σ</span>
                    <span className="font-mono-num ml-2 text-[#A87C0E]">−{fmtINR(totals.discount_total)}</span>
                  </div>
                )}
                {totals.delivery_total > 0 && (
                  <div className="rounded-md bg-[#FDF9EE]/40 border border-[#D4AF37]/20 px-2.5 py-1.5">
                    <span className="font-bold uppercase tracking-wider text-[10px]">Delivery Σ</span>
                    <span className="font-mono-num ml-2 text-[#0A3B24]">+{fmtINR(totals.delivery_total)}</span>
                  </div>
                )}
                {totals.gst_total > 0 && (
                  <div className="rounded-md bg-[#FDF9EE]/40 border border-[#D4AF37]/20 px-2.5 py-1.5">
                    <span className="font-bold uppercase tracking-wider text-[10px]">GST Σ</span>
                    <span className="font-mono-num ml-2 text-[#0A3B24]">+{fmtINR(totals.gst_total)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <span className="text-xs text-[#4B5563] mr-auto">
                {previewing ? "Pricing…" : "Live preview synced"}
              </span>
              <Button onClick={saveMenu} disabled={saving}
                      className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                      data-testid="menu-save-btn">
                <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                {editingId ? "Update Menu" : "Save Menu"}
              </Button>
              <Button onClick={() => downloadExport("pdf")} disabled={!editingId}
                      variant="outline"
                      className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold disabled:opacity-50"
                      data-testid="menu-export-pdf-btn">
                <FilePdf size={14} weight="bold" className="mr-1.5" /> Download PDF
              </Button>
              <Button onClick={() => downloadExport("excel")} disabled={!editingId}
                      className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold disabled:opacity-50"
                      data-testid="menu-export-excel-btn">
                <FileXls size={14} weight="bold" className="mr-1.5" /> Download Excel
              </Button>
            </div>
            {!editingId && (
              <div className="mt-2 text-[11px] text-[#A87C0E] text-right">
                Save the menu first to enable downloads.
              </div>
            )}
          </div>
        </div>

        {/* Saved Menus sidebar */}
        <aside className="lg:sticky lg:top-20 self-start">
          <div className="kostra-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="kostra-section-title text-base flex items-center gap-2">
                <FolderOpen size={14} weight="duotone" className="text-[#D4AF37]" />
                Saved Menus
              </h3>
              <span className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">
                {menus.length}
              </span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="menu-saved-list">
              {menus.length === 0 && (
                <div className="text-xs text-[#4B5563] py-6 text-center px-2 rounded-md border border-dashed border-[#E5E7EB] bg-[#F9FAFB]">
                  No saved menus yet. Build &amp; save your first menu →
                </div>
              )}
              {menus.map((m) => (
                <div key={m.id}
                     className={`rounded-lg border-2 p-3 transition ${
                       editingId === m.id
                         ? "border-[#D4AF37] bg-[#FDF9EE]"
                         : "border-[#E5E7EB] bg-white hover:border-[#0A3B24]/30"}`}
                     data-testid={`saved-menu-${m.id}`}>
                  <div className="font-display text-sm text-[#0A3B24] truncate">{m.name}</div>
                  {m.outlet && <div className="text-[11px] text-[#4B5563] truncate">{m.outlet}</div>}
                  <div className="mt-1.5 grid grid-cols-3 gap-1 text-[10px]">
                    <div>
                      <div className="text-[#4B5563]">Items</div>
                      <div className="font-mono-num font-bold text-[#0A3B24]">{m.totals?.item_count || 0}</div>
                    </div>
                    <div>
                      <div className="text-[#4B5563]">SP Σ</div>
                      <div className="font-mono-num font-bold text-[#0A3B24]">₹{Number(m.totals?.selling_price_total || 0).toFixed(0)}</div>
                    </div>
                    <div>
                      <div className="text-[#4B5563]">FC%</div>
                      <div className="font-mono-num font-bold text-emerald-700">{Number(m.totals?.blended_food_cost_pct || 0).toFixed(1)}%</div>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <button type="button" onClick={() => loadMenu(m)}
                            className="flex-1 text-[11px] font-semibold py-1 rounded-md bg-[#0A3B24] text-white hover:bg-[#0E5232]"
                            data-testid={`menu-load-${m.id}`}>
                      Load
                    </button>
                    <Link to={`/menu/${m.id}`}
                          className="flex-1 text-[11px] font-semibold py-1 rounded-md bg-[#D4AF37] text-[#0A3B24] hover:bg-[#C5A028] inline-flex items-center justify-center gap-1"
                          data-testid={`menu-preview-${m.id}`}>
                      <Eye size={11} weight="bold" /> Preview
                    </Link>
                    <button type="button" onClick={() => deleteMenu(m.id)}
                            className="text-[11px] font-semibold py-1 px-2 rounded-md text-[#DC2626] hover:bg-[#FBE8E8]"
                            data-testid={`menu-delete-${m.id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>

      <RecipePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        recipes={recipes}
        onAdd={addFromPicker}
      />

      <Footer />
    </div>
  );
}

function TotalStat({ label, value, accent, testid }) {
  const colorCls = accent === "gold"
    ? "text-[#A87C0E]"
    : accent === "ok"
      ? "text-emerald-700"
      : "text-[#0A3B24]";
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</div>
      <div className={`mt-0.5 font-mono-num font-bold text-sm ${colorCls}`}>{value}</div>
    </div>
  );
}

function PnLChip({ label, value, accent, testid }) {
  const colorCls = accent === "gold"
    ? "text-[#A87C0E]"
    : accent === "ok"
      ? "text-emerald-700"
      : accent === "warn"
        ? "text-amber-700"
        : "text-[#0A3B24]";
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</div>
      <div className={`font-mono-num font-bold text-sm ${colorCls}`}>{value}</div>
    </div>
  );
}
